// src/pages/Users/AddApiUser.tsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import colorConfigs from "../../configs/colorConfigs";
import { useFormik } from "formik";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import AxiosMain from "../../components/layout/AxiosMain";
import LoaderScreen from "../../components/layout/LoaderScreen";
import { AppAPiUrl, appModelPermission, appModelType, AppRoutesPath } from "../../configs/appData";
import Chip from "@mui/material/Chip";
import OutlinedInput from "@mui/material/OutlinedInput";
import TranslationGetHOC from "../../components/layout/TranslationGetHOC";
import AppPermissionHOC from "../../components/layout/AppPermissionHOC";
import { RootState } from "../../redux/store";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from 'yup';
import appRoutes from "../../routes/appRoutes";

interface Permission {
  id: number;
  name: string;
}

interface FormValues {
  name: string;
  username: string;
  expiry: Date | null;
  permissions: number[];
}

const AddApiUserComponentReady = (props: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [iComponetTranslate, setICTranslation] = useState<any[]>([]);
  const [isTransLoaded, setLoaded] = useState(false);
  const { appCountry } = useSelector((state: RootState) => state.appState);
  const [dropDownPermissions, setDropDownPermissions] = useState<any[]>([]);

  const t = (name: string) => {
    const ifExist = iComponetTranslate.filter((item: any) => item.label === name);
    if (ifExist.length > 0) {
      return ifExist[0].value ? ifExist[0].value : `No translate found (${ifExist[0].label})`;
    }
    return `No key found (${name})`;
  }

  const getDropDownPermissions = async () => {
    AxiosMain.get("/users/permission/").then((res) => {
      if (res.status === 200 ) {
        setDropDownPermissions(res.data);
      }
    }).catch((e) => {
      console.log("Something went wrong while fetching permissions");
    });
  }

  useEffect(() => {
    if (dropDownPermissions.length === 0) {
      getDropDownPermissions();
    }
    // eslint-disable-next-line
  }, []);

  const formSwalTranslation = {
    formSwalTitle: t("AreYouWanttoSave"),
    formSwalText: t("Youwontbeabletorevertthis"),
    fomrSwalCancel: t("Cancel"),
    formSwalSaved: t("YesSavedit"),
    formSwalDataSaved: t("YourDatahasbeensaved"),
    formSwalSubmited: t("Submitted"),
    formSwalWrong: t("SomethingWentWrong"),
  };

  const AddUserSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    username: Yup.string().required('Username is required'),
    expiry: Yup.date().nullable().required('Expiration date is required'),
    permissions: Yup.array().min(1, 'At least one permission is required'),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      name: "",
      username: "",
      expiry: null,
      permissions: [],
    },
    validationSchema: AddUserSchema,
    onSubmit: (values) => {
      setIsLoading(true);
      const payload = {
        name: values.name,
        username: values.username,
        expiry: values.expiry ? values.expiry.toISOString() : null,
        permissions: values.permissions,
      };
      AxiosMain.post(AppAPiUrl.AddAPIUsers, payload).then((res) => {
        if (res.status === 201) {
          Swal.fire({
            title: formSwalTranslation.formSwalDataSaved,
            text: `Secret Key: ${res.data.token}`,
            icon: "success"
          });
          setIsLoading(false);
          navigate(AppRoutesPath.ApiUsers)
          formik.resetForm();
          props.handleClose();
        }
      }).catch((e) => {
        setIsLoading(false);
        console.log("error ", e);
        const { response } = e;
        if (response && response.status === 400) {
          Swal.fire({
            title: "Error",
            text: response.data.detail || "Bad Request",
            icon: "error"
          });
        }
        else {
          Swal.fire({
            title: "Error",
            text: formSwalTranslation.formSwalWrong,
            icon: "error"
          });
        }
      })
    },
  });

  useEffect(() => {
    const { translateDataft } = props;
    const newCode = appCountry.toLocaleLowerCase();
    if (translateDataft && translateDataft.hasOwnProperty(`${newCode}`)) {
      setICTranslation(translateDataft[`${newCode}`]);
      setTimeout(() => {
        setLoaded(true);
      }, 100);
    }
    else {
      setICTranslation([]);
      setLoaded(true);
    }
  }, [props, appCountry]);

  return (
    <>
      <div className="cm-user-tabs-wrapper cm-global-tabs-wrapper">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent={"center"}>
          <Box className="fw-600" sx={{ color: "#012445", fontSize: "40px", lineHeight: "normal" }}>{t("AddAPIUser")}</Box>
        </Stack>
        <div className="cm-user-form-wrap" style={{ marginTop: "10px" }}>
          {isTransLoaded && isLoading ?
            <LoaderScreen />
            : (
              <Box sx={{ width: "100%" }}>
                <Box>
                  <form onSubmit={formik.handleSubmit}>
                    <Box>
                      <Grid container spacing={5} sx={{ paddingTop: "0" }}>
                        <Grid item md={12} sm={12} xs={12}>
                          <Box className="cm-form-inner-fields">
                            <Box className="form-mb-30">
                              <Box sx={{ flexGrow: 1 }}>
                                <Grid container spacing={3}>
                                  {/* Name Field */}
                                  <Grid item xs={12}>
                                    <Box sx={{ width: "100%" }}>
                                      <div className="form-label-name">
                                        {t("name")}
                                      </div>
                                      <FormControl sx={{ width: "100%" }}>
                                        <TextField
                                          className="cm-login-field"
                                          name="name"
                                          variant="outlined"
                                          value={formik.values.name}
                                          onChange={formik.handleChange}
                                          onBlur={formik.handleBlur}
                                          placeholder={t("name")}
                                        />
                                      </FormControl>
                                      {formik.errors.name && formik.touched.name && <p className="cm-form-error">{formik.errors.name}</p>}
                                    </Box>
                                  </Grid>

                                  {/* Username Field */}
                                  <Grid item xs={12}>
                                    <Box sx={{ width: "100%" }}>
                                      <div className="form-label-name">
                                        {t("username")}
                                      </div>
                                      <FormControl sx={{ width: "100%" }}>
                                        <TextField
                                          className="cm-login-field"
                                          name="username"
                                          variant="outlined"
                                          placeholder={t("username")}
                                          value={formik.values.username}
                                          onChange={formik.handleChange}
                                          onBlur={formik.handleBlur}
                                        />
                                      </FormControl>
                                      {formik.errors.username && formik.touched.username && <p className="cm-form-error">{formik.errors.username}</p>}
                                    </Box>
                                  </Grid>

                                  {/* Expiration Date Picker */}
                                  <Grid item xs={12}>
                                    <Box sx={{ width: "100%" }}>
                                      <div className="form-label-name">
                                        {t("expiration")}
                                      </div>
                                      <FormControl sx={{ width: "100%" }}>
                                        <DatePicker
                                          showTimeSelect
                                          dateFormat="d MMMM yyyy HH:mm"
                                          timeFormat="HH:mm"
                                          isClearable={true}
                                          selected={formik.values.expiry}
                                          onChange={(date: Date | null) => formik.setFieldValue('expiry', date)}
                                          placeholderText={t("expiration")}
                                          className="cm-date-picker-field"
                                          minDate={new Date()}
                                        />
                                      </FormControl>
                                      
                                      {formik.errors.expiry && formik.touched.expiry && <p className="cm-form-error">{formik.errors.expiry}</p>}
                                    </Box>
                                  </Grid>

                                  {/* Permissions Multi-Select */}
                                  <Grid item xs={12}>
                                    <Box sx={{ width: "100%" }}>
                                      <div className="form-label-name">
                                        {t("select_permissions")}
                                      </div>
                                      <FormControl sx={{ width: "100%" }}>
                                        <Select
                                          multiple
                                          name="permissions"
                                          value={formik.values.permissions}
                                          onChange={formik.handleChange}
                                          input={<OutlinedInput id="select-multiple-chip" label={t("select_permissions")} />}
                                          renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                              {selected.map((value: number) => {
                                                const permission = dropDownPermissions.find((perm) => perm.id === value);
                                                return <Chip key={value} label={permission ? permission.name : value} />;
                                              })}
                                            </Box>
                                          )}
                                        >
                                          {dropDownPermissions.map((perm: any) => (
                                            <MenuItem key={perm.id} value={perm.name}>
                                              {perm.name}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      {formik.errors.permissions && formik.touched.permissions && <p className="cm-form-error">{formik.errors.permissions}</p>}
                                    </Box>
                                  </Grid>

                                  {/* Submit Button */}
                                  <Grid item xs={12}>
                                    <Box>
                                      <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        justifyContent={"start"}
                                        className="form-submit-cancel-btn-wrapper"
                                      >
                                        <Button
                                          type="submit"
                                          variant="contained"
                                          className="cm-btn-style"
                                          disabled={isLoading}
                                          sx={{
                                            width: "100%",
                                            color: colorConfigs.btnColor.hoverbgWhite,
                                            background: colorConfigs.btnColor.bgColorGreen,
                                            "&:hover": {
                                              color: colorConfigs.btnColor.bgColorGreen,
                                              background: colorConfigs.btnColor.hoverbgWhite,
                                            },
                                          }}
                                        >
                                          {isLoading ? t("submitting") : t("submit")}
                                        </Button>
                                      </Stack>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </form>
                </Box>
              </Box>
            )}
        </div>
      </div>
    </>
  );
}

function AddApiUser(props: any) {
  const { isAllow } = props;
  const dependency = { require: ['Sidebar'] };
  const MainComponent = TranslationGetHOC(AddApiUserComponentReady, "users", isAllow, props, dependency);
  return isAllow ? <MainComponent /> : (<LoaderScreen msg="Loading ..." />);
}

export default AppPermissionHOC(AddApiUser, appModelPermission.M2MUSERS, appModelType.CREATE);
